import { useState, useLayoutEffect, useCallback, useEffect } from 'react';
import { useQueryMe } from '../graphql/query/getMe';
import { Permission } from '../types';
import { useMultiFilterStore } from '../components/MultiFilter/MultiFilter.store';

export type OrgAvailableData = { from?: string; to?: string };

export type OrganizationType =
  | { name: string; refId?: string; availableData?: OrgAvailableData }
  | null
  | undefined;

export type MeType = {
  active: boolean;
  email: string;
  fullName: string;
  refId?: string;
  role?: string | null;
  speciality?: string | null;
  forceResetPassword?: boolean;
  current?: { permissions: string[]; organization: OrganizationType } | null;
};

export type UseMeValueType = {
  isInited: boolean;
  isOrgSynced: boolean;
  currentOrg: OrganizationType;
  data: { me: MeType } | undefined;
  onOrgChange: (refId: string, name?: string) => void;
  can: (permission: Permission | Permission[]) => boolean;
  refetch: () => void;
};

export const useMe = () => {
  const [isInited, setIsInited] = useState(false);
  const { data, loading, refetch } = useQueryMe();
  const [currentOrg, setCurrentOrg] = useState<OrganizationType>(null);

  const can = useCallback(
    (permission: Permission | Permission[]) => {
      if (data?.me?.role === 'superadmin') return true;

      const toCheck = Array.isArray(permission) ? permission : [permission];
      return toCheck.every((p) => !!data?.me?.current?.permissions?.includes(p));
    },
    [data],
  );

  const handleSetCurrentOrg = useCallback(
    (refId?: string, name?: string) => {
      localStorage.setItem('hospital-refId', refId ?? '');
      setCurrentOrg(refId && name ? { name, refId } : null);
      useMultiFilterStore.persist.setOptions({
        name: `multiFilter-storage_${refId}`,
      });
      useMultiFilterStore.persist.rehydrate();
      refetch();
    },
    [refetch],
  );

  // The `/me` payload (and therefore `availableData`) lags behind the selected org
  // while the refetch is in flight. Consumers must not trust it until it catches up.
  const isOrgSynced =
    !currentOrg?.refId || currentOrg.refId === data?.me?.current?.organization?.refId;

  useEffect(() => {
    setCurrentOrg(data?.me?.current?.organization);
  }, [data?.me?.current?.organization]);

  useLayoutEffect(() => {
    if (loading || isInited) return;

    setIsInited(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return {
    isInited,
    isOrgSynced,
    currentOrg,
    data,
    can,
    onOrgChange: handleSetCurrentOrg,
    refetch,
  };
};
